import { Points, PrivacyTrackingState, PropertySort } from '__generated__/api';
import { URL_PARAM_TO_QUERY_ID_MAPPER } from 'stores/models';
import { adaptCountsFromStrings, adaptRangesFromStrings, adaptSelectionFromEnum, adaptSelectionFromString, adaptSelectionsFromStrings } from './adapter-utils';
import { getDateRangeInput, getDestinationInput, getFlexSearchCriteria, getRooms, getSearchCriteriaSecondaryBooleans } from './search-input-adapter';
import { MarketingChannels } from 'constants/marketing-channels';
import { MarketingPrices } from 'constants/marketing-prices';
const KNOWN_MARKETING_CHANNELS = Object.keys(MarketingChannels);
const getSanitizedExtendedCriteria = extendedCriteria => {
  const criteria = Object.assign({}, extendedCriteria);
  Object.keys(criteria).forEach(key => {
    const criteriaItem = criteria[key];
    if (criteriaItem === '' || Array.isArray(criteriaItem) && criteriaItem.length === 0) {
      // eslint-disable-next-line @typescript-eslint/no-dynamic-delete
      delete criteria[key];
    }
    if (KNOWN_MARKETING_CHANNELS.includes(key)) {
      criteria[key] = sanitizeURLParams(criteria, key);
    }
  });
  return criteria;
};
export function adaptSearchCriteria(context, extendedCriteria, searchPagination, extraSelections, analyticsSearch) {
  const criteria = getSanitizedExtendedCriteria(extendedCriteria);
  // flexSearchCriteria object won't be included in the primary object, when its value is null
  const flexSearchCriteria = getFlexSearchCriteria(criteria);
  const flexSearchCriteriaWrapper = flexSearchCriteria ? {
    flexSearchCriteria
  } : {};
  const searchCriteria = {
    primary: Object.assign(Object.assign({
      dateRange: getDateRangeInput(criteria),
      destination: getDestinationInput(criteria)
    }, flexSearchCriteriaWrapper), {
      rooms: getRooms(criteria)
    }),
    secondary: getSecondaryCriteria(context, extendedCriteria, searchPagination, extraSelections, analyticsSearch)
  };
  return searchCriteria;
}
const sortSecondarySelections = (selection, nextSelection) => selection.id.localeCompare(nextSelection.id);
export const getSecondaryCriteria = (context, extendedCriteria, searchPagination, extraSelections, analyticsSearch) => {
  const criteria = getSanitizedExtendedCriteria(extendedCriteria);
  const secondarySelections = [adaptSelectionFromString('categorySearch', 'categorySearch', criteria), adaptSelectionFromEnum('sort', 'sort', criteria, PropertySort), adaptSelectionFromString('chain', 'chain', criteria), adaptSelectionFromString('regionId', 'neighborhood', criteria), adaptSelectionFromString('poi', 'poi', criteria), adaptSelectionFromString('propertyName', 'hotelName', criteria), adaptSelectionFromString('hotelName', 'hotelName', criteria), adaptSelectionFromString('semdtl', 'semdtl', criteria), adaptSelectionFromString('theme', 'theme', criteria), adaptSelectionFromString('paandi', 'paandi', criteria), adaptSelectionFromString('userIntent', 'userIntent', criteria), adaptSelectionFromString('bedroom_count_gt', 'bedroom_count_gt', criteria), adaptSelectionFromString('us_bathroom_count_gt', 'us_bathroom_count_gt', criteria), adaptSelectionFromString('guestRating', 'guestRating', criteria), adaptSelectionFromString('comparePropertyIds', 'comparePropertyIds', criteria), adaptSelectionFromString('pricing_group', 'pricing_group', criteria), adaptSelectionFromString('rffrid', 'rffrid', criteria), ...adaptSearchId(analyticsSearch), adaptTrackingState(context), ...adaptSelectionsFromStrings('accessibility', 'accessibility', criteria), ...adaptSelectionsFromStrings('agencyBusinessModels', 'agencyBusinessModels', criteria), ...adaptSelectionsFromStrings('amenities', 'amenities', criteria), ...adaptSelectionsFromStrings('bedroomFilter', 'bedroomFilter', criteria), ...adaptSelectionsFromStrings('commissionTiers', 'commissionTiers', criteria), ...adaptSelectionsFromStrings('deals', 'deals', criteria), ...adaptSelectionsFromStrings('mealPlan', 'mealPlan', criteria), ...adaptSelectionsFromStrings('paymentType', 'paymentType', criteria), ...adaptSelectionsFromStrings('propertyStyles', 'propertyStyles', criteria), ...adaptSelectionsFromStrings('bedType', 'bedType', criteria), ...adaptSelectionsFromStrings('cleaningAndSafetyPractices', 'cleaningAndSafetyPractices', criteria), ...adaptSelectionsFromStrings('rewards', 'rewards', criteria), ...adaptSelectionsFromStrings('starRating', 'star', criteria), ...adaptSelectionsFromStrings('location_group', 'location_group', criteria), ...adaptSelectionsFromStrings('property_type_group', 'property_type_group', criteria), ...adaptSelectionsFromStrings('accessibility_features_group', 'accessibility_features_group', criteria), ...adaptSelectionsFromStrings('booking_options_group', 'booking_options_group', criteria), ...adaptSelectionsFromStrings('discounts_group', 'discounts_group', criteria), ...adaptSelectionsFromStrings('amenities_facilities_group', 'amenities_facilities_group', criteria), ...adaptSelectionsFromStrings('free_cancellation_group', 'free_cancellation_group', criteria), ...adaptSelectionsFromStrings('house_rules_group', 'house_rules_group', criteria), ...adaptSelectionsFromStrings('nearby_activities_group', 'nearby_activities_group', criteria), ...adaptSelectionsFromStrings('neighborhood_group', 'neighborhood_group', criteria), ...adaptSelectionsFromStrings('premier_host_group', 'premier_host_group', criteria), ...adaptSelectionsFromStrings('properties_good_for_group', 'properties_good_for_group', criteria), ...adaptSelectionsFromStrings('property_reviews_group', 'property_reviews_group', criteria), ...adaptSelectionsFromStrings('rooms_spaces_group', 'rooms_spaces_group', criteria), ...adaptSelectionsFromStrings('safety_group', 'safety_group', criteria), ...adaptSelectionsFromStrings('virtual_tour_group', 'virtual_tour_group', criteria), ...adaptSelectionsFromStrings('star', 'star', criteria), ...adaptSelectionsFromStrings('structureTypes', 'lodging', criteria), ...adaptSelectionsFromStrings('stayType', 'stayType', criteria), ...adaptSelectionsFromStrings('lodging', 'lodging', criteria), ...adaptSelectionsFromStrings('travelerType', 'travelerType', criteria), ...adaptSelectionsFromStrings('bed_type_group', 'bed_type_group', criteria), ...adaptSelectionsFromStrings('stay_options_group', 'stay_options_group', criteria), ...adaptSelectionsFromStrings('hotel_brand', 'hotel_brand', criteria), ...adaptSelectionsFromStrings('multi_neighborhood_group', 'multi_neighborhood_group', criteria), ...adaptMarketingInfo(criteria), ...adaptPropertyShopOptions(criteria.useRewards), ...adaptExtraSelections(extraSelections), ...adaptPinnedPropertyRatePlanSelection(criteria)];
  const filteredSelections = secondarySelections.filter(selection => Boolean(selection)).sort(sortSecondarySelections);
  return {
    counts: [...adaptCountsFromStrings('bedroomFilter', 'bedroomFilter', criteria), ...adaptPagination(searchPagination), ...adaptCountsFromStrings('price', 'price', criteria)].filter(count => count),
    booleans: getSearchCriteriaSecondaryBooleans(criteria, URL_PARAM_TO_QUERY_ID_MAPPER),
    selections: filteredSelections,
    ranges: [...adaptRangesFromStrings('price', 'price', criteria), ...adaptRangesFromStrings('total_price', 'total_price', criteria), ...adaptRangesFromStrings('nightly_price', 'nightly_price', criteria)]
  };
};
const sanitizeURLParams = (criteria, key) => {
  const value = criteria[key];
  return value && typeof value !== 'string' && value.length > 0 ? value.filter(p => p)[0] : value;
};
function adaptMarketingInfo(criteria) {
  const marketingInfo = [];
  Object.keys(criteria).forEach(id => {
    const value = criteria[id];
    if (value && (id.toLowerCase() in MarketingChannels || id.toLowerCase() in MarketingPrices)) {
      marketingInfo.push({
        id,
        value
      });
    }
  });
  return marketingInfo;
}
function adaptPropertyShopOptions(useRewards) {
  const selections = [];
  let points;
  if (useRewards === 'true') {
    points = Points.SHOP_WITH_POINTS;
  } else if (useRewards === 'false') {
    points = Points.SHOP_WITHOUT_POINTS;
  }
  if (points) {
    selections.push({
      id: 'useRewards',
      value: points
    });
  }
  return selections;
}
function adaptExtraSelections(extraSelections) {
  return extraSelections !== null && extraSelections !== void 0 ? extraSelections : [];
}
function adaptPagination(searchPagination) {
  if (searchPagination === null || searchPagination === void 0 ? void 0 : searchPagination.size) {
    return [{
      id: 'resultsStartingIndex',
      value: searchPagination.startingIndex || 0
    }, {
      id: 'resultsSize',
      value: searchPagination.size
    }];
  }
  return [];
}
function adaptTrackingState(context) {
  const id = 'privacyTrackingState';
  return {
    id,
    value: context.privacyTrackingState || PrivacyTrackingState.CAN_NOT_TRACK
  };
}
function adaptPinnedPropertyRatePlanSelection(criteria) {
  return [adaptSelectionFromString('rateplanid', 'rateplanid', criteria), adaptSelectionFromString('mpm', 'mpm', criteria),
  // inventory provider id
  adaptSelectionFromString('mpn', 'mpn', criteria),
  // room type id
  adaptSelectionFromString('mpo', 'mpo', criteria),
  // business model
  adaptSelectionFromString('mpp', 'mpp', criteria) // cheapest flag -> 0=no, 1=yes
  ];
}
function adaptSearchId(analyticsSearch) {
  const {
    searchId
  } = analyticsSearch || {};
  return searchId ? [{
    id: 'searchId',
    value: searchId
  }] : [];
}